<template>
  <div class="profile-page">
    <AppNavbar />

    <main class="container profile-content">
      <div class="page-header">
        <div class="page-header-top">
          <h1 class="page-title">My Account</h1>
          <div class="header-actions">
            <router-link :to="{ name: 'order-status' }" class="orders-link-btn">
              <i class="ri-file-list-3-line"></i>
              My Orders
            </router-link>
            <!-- <router-link
              v-if="!isSelfCheckoutUser"
              :to="{ name: 'dashboard' }"
              class="orders-link-btn"
            >
              <i class="ri-dashboard-line"></i>
              My Dashboard
            </router-link> -->
          </div>
        </div>

        <p class="page-subtitle">
          Manage your profile, addresses, and verification details
        </p>
      </div>

      <!--
        UNIFIED CARDS GRID
        All cards (Profile, KYC+Bank, Address, Beneficiaries) live in ONE grid now.
        Why: previously Profile/KYC were in one grid ("main-grid") and
        Address/Beneficiaries were in a separate grid ("bottom-grid"). That meant
        if KYC wasn't shown, Address still got pushed to a brand new row below,
        leaving an empty gap next to Profile instead of filling it.
        Using a single 2-column grid lets whichever card is next in the DOM
        flow into the next open cell (beside Profile if KYC is hidden, etc.),
        while keeping every card's width identical (fixed 1fr/1fr columns —
        never stretched to fill the row alone).
      -->
      <div class="cards-grid">
        <!-- MY PROFILE CARD -->
        <div class="card profile-card">
          <div class="card-header-row">
            <i class="ri-user-line card-icon"></i>
            <span class="card-label">MY PROFILE</span>
          </div>
          <div class="avatar-row">
            <div class="avatar-circle">{{ getInitials(profile.fullName) }}</div>
            <div>
              <p class="profile-name">{{ profile.fullName || "—" }}</p>
              <span class="role-badge">{{
                profile.role || "DISTRIBUTOR"
              }}</span>
            </div>
          </div>
          <div class="info-list">
            <div class="info-row">
              <span class="info-label">Mobile</span>
              <span class="info-value">{{ profile.mobile || "—" }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Email</span>
              <span class="info-value">{{ profile.email || "—" }}</span>
            </div>
            <div v-if="profile.dob" class="info-row">
              <span class="info-label">Date of Birth</span>
              <span class="info-value">{{ profile.dob || "—" }}</span>
            </div>
            <div v-if="!isCustomerUser" class="info-row">
              <span class="info-label">BA ID</span>
              <span class="info-value">{{
                profile.distributorCode || "—"
              }}</span>
            </div>
            <div v-if="!isCustomerUser" class="info-row">
              <span class="info-label">Referrer ID</span>
              <span class="info-value">{{ profile.referenceCode || "—" }}</span>
            </div>
            <div class="info-row no-border">
              <span class="info-label">Member Since</span>
              <span class="info-value">{{ profile.createdDate || "—" }}</span>
            </div>
          </div>
        </div>

        <!-- KYC + BANK CARD (combined) -->
        <div v-if="showKycCard" class="card kyc-bank-card">
          <div class="card-header-row">
            <i class="ri-shield-check-line card-icon"></i>
            <span class="card-label">KYC & VERIFICATION</span>
          </div>

          <!-- KYC status rows -->
          <div class="info-list">
            <div class="info-row">
              <span class="info-label">Mobile</span>
              <span
                :class="profile.mobileVerified ? 'verified-tag' : 'pending-tag'"
              >
                <i
                  :class="
                    profile.mobileVerified
                      ? 'ri-checkbox-circle-fill'
                      : 'ri-time-line'
                  "
                ></i>
                {{ profile.mobileVerified ? "Verified" : "Pending" }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">Aadhaar</span>
              <span
                :class="
                  profile.aadhaarVerified ? 'verified-tag' : 'pending-tag'
                "
              >
                <i
                  :class="
                    profile.aadhaarVerified
                      ? 'ri-checkbox-circle-fill'
                      : 'ri-time-line'
                  "
                ></i>
                {{ profile.aadhaarVerified ? "Verified" : "Pending" }}
              </span>
            </div>
            <div
              class="info-row"
              :class="{
                'no-border': !profile.panNumber && !profile.aadhaarMasked,
              }"
            >
              <span class="info-label">PAN</span>
              <span
                :class="profile.panVerified ? 'verified-tag' : 'pending-tag'"
              >
                <i
                  :class="
                    profile.panVerified
                      ? 'ri-checkbox-circle-fill'
                      : 'ri-time-line'
                  "
                ></i>
                {{ profile.panVerified ? "Verified" : "Pending" }}
              </span>
            </div>
            <div v-if="profile.panNumber" class="info-row">
              <span class="info-label">PAN Number</span>
              <span class="info-value mono">{{ profile.panNumber }}</span>
            </div>
            <div v-if="profile.aadhaarMasked" class="info-row no-border">
              <span class="info-label">Aadhaar No.</span>
              <span class="info-value mono">{{ profile.aadhaarMasked }}</span>
            </div>
          </div>

          <!-- BANK SECTION divider -->
          <div class="section-divider">
            <i class="ri-bank-line"></i>
            <span>BANK ACCOUNT</span>
          </div>

          <!-- Bank verified state -->
          <template v-if="profile.bankVerified">
            <!-- <div class="info-row">
              <span class="info-label">Bank Name Holder</span>
              <span class="info-value">{{
                profile.bank.accountHolder || "—"
              }}</span>
            </div> -->
            <div class="info-row no-border">
              <span class="info-label">Status</span>
              <span class="verified-tag"
                ><i class="ri-checkbox-circle-fill"></i> Verified</span
              >
            </div>
            <!-- <p v-if="bankStatusMessage" class="bank-status-message">
              {{ bankStatusMessage }}
            </p> -->
            <!-- <div class="info-list">
              <div class="info-row">
                <span class="info-label">Account Holder</span>
                <span class="info-value">{{ profile.bank.accountHolder }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Account Number</span>
                <span class="info-value mono">{{
                  profile.bank.maskedAccount
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">IFSC Code</span>
                <span class="info-value mono">{{ profile.bank.ifsc }}</span>
              </div>
              <div class="info-row no-border">
                <span class="info-label">Status</span>
                <span class="verified-tag"
                  ><i class="ri-checkbox-circle-fill"></i> Verified</span
                >
              </div>
            </div> -->
          </template>

          <!-- Bank not verified -->
          <template v-else>
            <div class="pending-bank-notice">
              <i class="ri-error-warning-line"></i>
              <div>
                <p class="notice-title">Bank verification pending</p>
                <p class="notice-sub">
                  Complete within 30 days of registration to receive payouts.
                </p>
              </div>
            </div>
            <button
              type="button"
              class="verify-bank-btn"
              @click="openBankModal"
            >
              <i class="ri-bank-card-line"></i> Verify Bank Account
            </button>
          </template>
        </div>

        <!-- ADDRESS CARD -->
        <div v-if="showAddressCard" class="card">
          <div class="card-header-row">
            <i class="ri-map-pin-2-line card-icon"></i>
            <span class="card-label">ADDRESS</span>
          </div>
          <div v-if="!customerAddresses.length" class="empty-state">
            <i class="ri-map-pin-line"></i>
            <p>
              No address saved yet. Complete Aadhaar verification during
              registration to auto-populate.
            </p>
          </div>
          <div v-else class="address-list">
            <div
              v-for="(address, index) in customerAddresses"
              :key="address.addressId || index"
              class="addr-display"
            >
              <div class="addr-icon-wrap">
                <i class="ri-home-4-line"></i>
              </div>
              <div class="addr-text">
                <p class="addr-name">{{ address.contactPerson || "—" }}</p>
                <p class="addr-line">{{ address.addressLine || "—" }}</p>
                <p class="addr-meta">{{ address.cityStatePincode || "—" }}</p>
                <p v-if="address.country" class="addr-meta">
                  {{ address.country }}
                </p>
                <p v-if="address.contactPhoneNumber" class="addr-meta">
                  {{ address.contactPhoneNumber }}
                </p>
              </div>
              <span v-if="address.isDefault" class="primary-pill">Primary</span>
            </div>
          </div>
        </div>

        <!-- BENEFICIARIES CARD -->
        <div v-if="showBeneficiariesCard" class="card">
          <div class="card-header-row">
            <i class="ri-user-star-line card-icon"></i>
            <span class="card-label">BENEFICIARIES</span>
            <button
              class="header-action-btn"
              type="button"
              @click="openBeneficiaryModal"
            >
              <i class="ri-add-line"></i> Add Beneficiary
            </button>
          </div>

          <p
            v-if="beneficiaryApiError"
            class="field-error beneficiary-api-error"
          >
            {{ beneficiaryApiError }}
          </p>

          <div v-if="isLoadingBeneficiaries" class="empty-state">
            <i class="ri-loader-4-line spin-icon"></i>
            <p>Loading beneficiaries...</p>
          </div>
          <div v-else-if="!beneficiaries.length" class="empty-state">
            <i class="ri-user-add-line"></i>
            <p>No beneficiaries added yet.</p>
          </div>
          <div v-else-if="beneficiaries.length" class="beneficiary-list">
            <div
              v-for="(b, index) in beneficiaries"
              :key="b.id || index"
              class="beneficiary-item"
            >
              <div class="beneficiary-avatar">
                {{ getInitials(b.fullName) }}
              </div>
              <div class="beneficiary-info">
                <p class="b-name">{{ b.fullName || "-" }}</p>
                <div class="b-detail-row">
                  <span class="b-key">Email</span>
                  <span class="b-val">{{ b.emailId || "-" }}</span>
                </div>
                <div class="b-detail-row">
                  <span class="b-key">Mobile</span>
                  <span class="b-val">{{ b.mobileNumber || "-" }}</span>
                </div>
                <div class="b-detail-row">
                  <span class="b-key">Relationship</span>
                  <span class="b-val">{{ b.relationship || "-" }}</span>
                </div>
              </div>
              <button
                type="button"
                title="Edit Beneficiary"
                class="icon-btn"
                @click="openEditBeneficiaryModal(b)"
              >
                <i class="ri-edit-2-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <BeneficiaryModal
        :open="showBeneficiaryModal"
        :saving="isSavingBeneficiary"
        :mode="beneficiaryModalMode"
        :initial-data="editingBeneficiary"
        @cancel="closeBeneficiaryModal"
        @submit="addBeneficiary"
      />

      <transition name="fade">
        <div
          v-if="showBankModal"
          class="modal-backdrop"
          @click.self="requestCloseBankModal"
        >
          <div class="modal-card bank-modal">
            <div class="modal-header-row">
              <h3 class="modal-title">Add Bank Details</h3>
              <button
                type="button"
                class="icon-btn"
                aria-label="Close"
                :disabled="isSavingBank"
                @click="requestCloseBankModal"
              >
                <i class="ri-close-line"></i>
              </button>
            </div>

            <div v-if="isSavingBank" class="bank-modal-loading">
              <div class="bank-modal-loading-inner">
                <span class="loading-spinner"></span>
                <p>Verifying bank details...</p>
              </div>
            </div>

            <div class="form-box bank-modal-form">
              <div class="fgroup">
                <label>Account Number <span class="req">*</span></label>
                <input
                  v-model="bankForm.accountNumber"
                  type="text"
                  inputmode="numeric"
                  placeholder="Enter account number"
                  :disabled="isSavingBank"
                />
              </div>
              <div class="fgroup">
                <label>Confirm Account Number <span class="req">*</span></label>
                <input
                  v-model="bankForm.confirmAccountNumber"
                  type="text"
                  inputmode="numeric"
                  placeholder="Re-enter account number"
                  :disabled="isSavingBank"
                />
              </div>
              <div class="fgroup">
                <label>IFSC Code <span class="req">*</span></label>
                <input
                  v-model="bankForm.ifsc"
                  type="text"
                  placeholder="e.g. SBIN0001234"
                  maxlength="11"
                  @input="bankForm.ifsc = bankForm.ifsc.toUpperCase()"
                  :disabled="isSavingBank"
                />
              </div>
              <p v-if="bankWarning" class="field-warning">{{ bankWarning }}</p>
              <p v-if="bankError" class="field-error">{{ bankError }}</p>

              <div class="form-actions">
                <button
                  type="button"
                  class="cancel-btn"
                  :disabled="isSavingBank"
                  @click="requestCloseBankModal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="save-btn"
                  :disabled="isSavingBank"
                  @click="submitBankVerification"
                >
                  {{ isSavingBank ? "Saving..." : "Confirm" }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </main>

    <Footer />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import {
  addBankDetails,
  addCustomerBeneficiary,
  getCustomer,
  getCustomerBeneficiaries,
} from "rdep-ecom-sdk";
import AppNavbar from "../AppNavbar.vue";
import Footer from "../FooterView.vue";
import BeneficiaryModal from "./BeneficiaryModal.vue";

// ─── PROFILE ───
const profile = ref({
  fullName: "",
  role: "DISTRIBUTOR",
  mobile: "",
  email: "",
  dob: "",
  distributorCode: "",
  referenceCode: "",
  createdDate: "",
  mobileVerified: false,
  aadhaarVerified: false,
  panVerified: false,
  orderUpdateOpted: false,
  panNumber: "",
  aadhaarMasked: "",
  bankVerified: false,
  bank: { accountHolder: "", maskedAccount: "", ifsc: "" },
});

// ─── ADDRESS ───
const customerAddresses = ref([]);
const hasAddressFromApi = ref(false);

// ─── BANK ───
const showBankModal = ref(false);
const isSavingBank = ref(false);
const bankError = ref("");
const bankWarning = ref("");
const bankStatusMessage = ref("");
const bankForm = ref({
  accountNumber: "",
  confirmAccountNumber: "",
  ifsc: "",
});

// ─── BENEFICIARIES ───
const beneficiaries = ref([]);
const showBeneficiaryModal = ref(false);
const isLoadingBeneficiaries = ref(false);
const isSavingBeneficiary = ref(false);
const beneficiaryApiError = ref("");
const beneficiaryModalMode = ref("add");
const editingBeneficiary = ref({});

const isCustomerUser = computed(
  () => String(profile.value.role || "").toUpperCase() === "CUSTOMER",
);
// const isSelfCheckoutUser = computed(() => {
//   try {
//     const rawAuth = window.localStorage.getItem("auth.currentUser");
//     const authPayload = rawAuth ? JSON.parse(rawAuth) : {};
//     const user = authPayload?.data || authPayload || {};
//     const roleCandidates = [
//       user?.userType,
//       user?.role,
//       user?.loginRole,
//       user?.customerType,
//       profile.value.role,
//     ]
//       .map(normalizeRoleToken)
//       .filter(Boolean);

//     return roleCandidates.some((role) => role.includes("SELF_CHECKOUT"));
//   } catch (error) {
//     return false;
//   }
// });
const showKycCard = computed(() => !isCustomerUser.value);
const showBeneficiariesCard = computed(() => !isCustomerUser.value);
const showAddressCard = computed(
  () => hasAddressFromApi.value && customerAddresses.value.length > 0,
);

// ─── HELPERS ───
function getInitials(name) {
  if (!name) return "?";
  return name
    .trim()
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}
function formatDate(raw) {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d)) return raw;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
function toVerifiedFlag(value) {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    String(value || "").toLowerCase() === "true"
  );
}

function normalizeRoleToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function resolveDisplayRole(user, fallbackRole = "") {
  const userPayload = user || {};

  if (userPayload.distributor === false) {
    return "CUSTOMER";
  }
  if (userPayload.distributor === true) {
    return "DISTRIBUTOR";
  }

  const normalizedUserType = normalizeRoleToken(userPayload.userType);

  if (normalizedUserType === "SELF_CHECKOUT_USER") {
    return "CUSTOMER";
  }

  const roleCandidates = [
    userPayload.userType,
    userPayload.role,
    userPayload.customerType,
    userPayload.loginRole,
    userPayload.accountType,
    userPayload.userCategory,
    fallbackRole,
  ]
    .map(normalizeRoleToken)
    .filter(Boolean);

  const isCustomerLike = roleCandidates.some(
    (role) =>
      role.includes("CUSTOMER") ||
      role.includes("SELF_CHECKOUT") ||
      role.includes("CWS"),
  );

  if (isCustomerLike) return "CUSTOMER";

  const isDistributorLike = roleCandidates.some((role) =>
    role.includes("DISTRIBUTOR"),
  );
  if (isDistributorLike) return "DISTRIBUTOR";

  return "DISTRIBUTOR";
}

function resolveCustomerPayload(response) {
  const root = response?.data || response || {};
  return (
    root?.customer ||
    root?.customerDetails ||
    root?.customerData ||
    root?.profile ||
    root?.data ||
    root
  );
}

// ─── LOAD PROFILE ───
async function loadProfile() {
  let user = null;
  let profileFromApi = false;
  let fallbackRole = "";

  try {
    const rawAuth = window.localStorage.getItem("auth.currentUser");
    const authUser = rawAuth ? JSON.parse(rawAuth) : null;
    fallbackRole = String(authUser?.role || authUser?.userType || "");
  } catch (error) {
    fallbackRole = "";
  }

  try {
    const customerResponse = await getCustomer();
    const resolved = resolveCustomerPayload(customerResponse);
    if (
      resolved &&
      typeof resolved === "object" &&
      Object.keys(resolved).length
    ) {
      user = resolved;
      profileFromApi = true;
    }
  } catch (error) {
    /* fall back */
  }

  if (!user) {
    try {
      const raw = localStorage.getItem("auth.currentUser");
      if (raw) user = JSON.parse(raw);
    } catch {
      /* ignore */
    }
  }

  if (!user) return;
  hasAddressFromApi.value = profileFromApi;
  applyUserToProfile(user?.data || user, {
    fromApi: profileFromApi,
    fallbackRole,
  });
}

function applyUserToProfile(user, options = {}) {
  if (!user) return;
  const { fromApi = false, fallbackRole = "" } = options;
  profile.value.fullName =
    user.firstName || user.name || user.fullName || user.customerName || "";
  profile.value.mobile =
    user.contactNumber || user.mobileNumber || user.mobile || "";
  profile.value.email = user.email || user.mailId || "";
  profile.value.dob = user.dateOfBirth || user.dob || "";
  profile.value.distributorCode =
    user.username || user.distributorCode || user.businessAssociateId || "";
  profile.value.referenceCode = user.referenceCode || "";
  profile.value.role = resolveDisplayRole(user, fallbackRole);
  profile.value.createdDate = formatDate(
    user.createdAt || user.registrationDate || user.createdDate || "",
  );
  profile.value.mobileVerified = toVerifiedFlag(
    user.mobileVerified ?? user.isMobileVerified,
  );
  profile.value.aadhaarVerified = toVerifiedFlag(
    user.aadhaarVerified ?? user.isAadhaarVerified,
  );
  profile.value.panVerified = toVerifiedFlag(
    user.panVerified ?? user.isPanVerified,
  );
  profile.value.panNumber = user.maskedPan || user.panNumber || "";
  profile.value.aadhaarMasked = user.maskedAadhaar || user.aadhaarMasked || "";
  const bank = user.bankDetails || user.bank || {};
  profile.value.bankVerified = toVerifiedFlag(
    user.bankAccountValidated ??
      user.bankVerified ??
      bank.bankAccountValidated ??
      bank.verified ??
      bank.isVerified,
  );
  if (profile.value.bankVerified) {
    profile.value.bank.accountHolder =
      bank.accountHolder ||
      bank.bankAccountHolderName ||
      bank.nameAtBank ||
      user.nameAtBank ||
      "";
    profile.value.bank.maskedAccount =
      bank.maskedAccount || bank.accountNumber || "";
    profile.value.bank.ifsc = bank.ifsc || bank.bankIfsc || "";
  }
  const addressList = Array.isArray(user.addresses)
    ? user.addresses
    : Array.isArray(user.addressList)
    ? user.addressList
    : [];

  const allAddresses = [
    ...addressList,
    ...(user.address ? [user.address] : []),
  ];

  if (!fromApi || !allAddresses.length) {
    customerAddresses.value = [];
    return;
  }

  const seenAddressIds = new Set();
  customerAddresses.value = allAddresses
    .map((address) => ({
      addressId: address.addressId || address.id || null,
      addressLine: [
        address.addressLine1,
        address.addressLine2,
        address.addressLine3,
      ]
        .filter(Boolean)
        .join(", "),
      cityStatePincode: [address.city, address.state, address.pinCode]
        .filter(Boolean)
        .join(", "),
      country: address.country || "",
      contactPerson: address.contactPerson || address.fullName || "",
      contactPhoneNumber:
        address.contactPhoneNumber || address.phone || address.mobile || "",
      isDefault: Boolean(address.defaultDeliveryAddress),
    }))
    .filter((address) => {
      const key = String(address.addressId || "").trim();
      if (!key) return true;
      if (seenAddressIds.has(key)) return false;
      seenAddressIds.add(key);
      return true;
    })
    .sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
}

// ─── BANK VERIFICATION ───
function openBankModal() {
  bankError.value = "";
  bankWarning.value = "";
  showBankModal.value = true;
}

function requestCloseBankModal() {
  if (isSavingBank.value) return;
  closeBankModal();
}

function closeBankModal(force = false) {
  if (isSavingBank.value && !force) return;
  showBankModal.value = false;
  bankError.value = "";
  bankWarning.value = "";
  bankForm.value = {
    accountNumber: "",
    confirmAccountNumber: "",
    ifsc: "",
  };
}

async function submitBankVerification() {
  bankError.value = "";
  bankWarning.value = "";
  const f = bankForm.value;
  if (!f.accountNumber || !f.confirmAccountNumber || !f.ifsc) {
    bankError.value = "Please fill all fields.";
    return;
  }
  if (f.accountNumber !== f.confirmAccountNumber) {
    bankError.value = "Account numbers do not match.";
    return;
  }
  isSavingBank.value = true;
  try {
    const response = await addBankDetails({
      bankAccountNumber: f.accountNumber.trim(),
      bankIfsc: f.ifsc.trim().toUpperCase(),
    });
    const data = response?.data ?? response ?? {};
    const statusCode = String(data?.statusCode ?? "").trim();
    const statusMessage = String(
      data?.statusMessage || data?.message || "",
    ).trim();

    if (statusCode === "200") {
      const nameAtBank = String(data?.nameAtBank || "").trim();
      profile.value.bank = {
        accountHolder: nameAtBank,
        maskedAccount: "••••••••" + f.accountNumber.slice(-4),
        ifsc: f.ifsc.toUpperCase(),
      };
      profile.value.bankVerified = true;
      bankStatusMessage.value =
        statusMessage || "Bank account details verified successfully.";
      closeBankModal(true);
    } else if (statusCode === "400") {
      bankWarning.value =
        data?.statusMessage ||
        data?.message ||
        "Bank details are invalid. Please correct and try again.";
    } else {
      bankError.value =
        data?.statusMessage ||
        data?.message ||
        "Verification failed. Please try again.";
    }
  } catch (e) {
    const errorData = e?.response?.data || {};
    const errorStatusCode = String(errorData?.statusCode ?? "").trim();
    if (errorStatusCode === "400") {
      bankWarning.value =
        errorData?.statusMessage ||
        errorData?.message ||
        "Bank details are invalid. Please correct and try again.";
    } else {
      bankError.value =
        errorData?.statusMessage || e?.message || "Bank verification failed.";
    }
  } finally {
    isSavingBank.value = false;
  }
}

// ─── BENEFICIARIES ───
function normalizeBeneficiariesResponse(response) {
  const root = response?.data || response || {};

  const hasDirectBeneficiaryFields =
    root &&
    typeof root === "object" &&
    (root.fullName || root.emailId || root.mobileNumber || root.relationship);

  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.beneficiaries)) return root.beneficiaries;
  if (Array.isArray(root?.beneficiaryList)) return root.beneficiaryList;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root?.records)) return root.records;
  if (hasDirectBeneficiaryFields) return [root];
  return [];
}

function normalizeBeneficiaryItem(item = {}, index = 0) {
  return {
    id: item.id || item.beneficiaryId || index,
    fullName: item.fullName || item.name || "",
    emailId: item.emailId || item.email || "",
    mobileNumber: item.mobileNumber || item.mobile || "",
    relationship: item.relationship || "",
  };
}

async function loadBeneficiaries() {
  isLoadingBeneficiaries.value = true;
  beneficiaryApiError.value = "";
  try {
    const response = await getCustomerBeneficiaries();
    const list = normalizeBeneficiariesResponse(response).map(
      normalizeBeneficiaryItem,
    );
    beneficiaries.value = list;
  } catch (error) {
    beneficiaryApiError.value =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to load beneficiaries.";
  } finally {
    isLoadingBeneficiaries.value = false;
  }
}

function openBeneficiaryModal() {
  beneficiaryApiError.value = "";
  beneficiaryModalMode.value = "add";
  editingBeneficiary.value = {};
  showBeneficiaryModal.value = true;
}

function openEditBeneficiaryModal(beneficiary) {
  beneficiaryApiError.value = "";
  beneficiaryModalMode.value = "edit";
  editingBeneficiary.value = {
    fullName: beneficiary?.fullName || "",
    emailId: beneficiary?.emailId || "",
    mobileNumber: beneficiary?.mobileNumber || "",
    relationship: beneficiary?.relationship || "",
  };
  showBeneficiaryModal.value = true;
}

function closeBeneficiaryModal() {
  if (isSavingBeneficiary.value) return;
  showBeneficiaryModal.value = false;
  beneficiaryModalMode.value = "add";
  editingBeneficiary.value = {};
}

async function addBeneficiary(payload) {
  beneficiaryApiError.value = "";
  isSavingBeneficiary.value = true;
  try {
    // As requested, Update also uses the same add beneficiary API.
    await addCustomerBeneficiary(payload);
    showBeneficiaryModal.value = false;
    beneficiaryModalMode.value = "add";
    editingBeneficiary.value = {};
    await loadBeneficiaries();
  } catch (error) {
    beneficiaryApiError.value =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      error?.message ||
      (beneficiaryModalMode.value === "edit"
        ? "Failed to update beneficiary."
        : "Failed to add beneficiary.");
  } finally {
    isSavingBeneficiary.value = false;
  }
}

onMounted(async () => {
  await loadProfile();
  await loadBeneficiaries();
});
</script>

<style scoped>
/* ── BASE ── */
.profile-page {
  background: #f4f7fc;
  min-height: 100vh;
}
.container {
  width: 92%;
  max-width: 1140px;
  margin: 0 auto;
}
.profile-content {
  padding: 40px 0 72px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.page-header {
  margin-bottom: 4px;
}
.page-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.page-title {
  margin: 0 0 4px;
  font-size: 26px;
  font-weight: 800;
  color: #0a2240;
  letter-spacing: -0.4px;
}
.orders-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: 1.5px solid #003da5;
  border-radius: 999px;
  text-decoration: none;
  background: #fff;
  color: #003da5;
  font-size: 13px;
  font-weight: 700;
  transition: all 0.2s;
}
.orders-link-btn:hover {
  background: #003da5;
  color: #fff;
}
.page-subtitle {
  margin: 0;
  font-size: 14px;
  color: #6b7f99;
}

/* ── LAYOUT ──
   Single unified grid for ALL cards (Profile, KYC+Bank, Address, Beneficiaries).
   grid-auto-flow keeps cards packed left-to-right, top-to-bottom, so a card
   never stretches to fill an empty column, and the next available card
   (e.g. Address) fills the empty slot beside an existing card instead of
   dropping to a new row. Works cleanly for any combination: 1, 2, 3 or 4
   cards visible. */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-flow: row dense;
  gap: 20px;
  align-items: start;
}

/* ── CARD ── */
.card {
  background: #fff;
  border: 1px solid #dce7f6;
  border-radius: 16px;
  padding: 24px;
  position: relative;
  overflow: hidden;
}
.card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: #ffc72c;
  border-radius: 16px 16px 0 0;
}

.card-header-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
}
.card-icon {
  font-size: 15px;
  color: #003da5;
}
.card-label {
  font-size: 11px;
  font-weight: 800;
  color: #003da5;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  flex: 1;
}
.header-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 14px;
  border: 1.5px solid #003da5;
  background: transparent;
  color: #003da5;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}
.header-action-btn:hover {
  background: #003da5;
  color: #fff;
}

/* ── AVATAR ── */
.avatar-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
  padding-bottom: 18px;
  border-bottom: 1px solid #edf2f7;
}
.avatar-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #003da5;
  color: #fff;
  font-size: 17px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.profile-name {
  margin: 0 0 5px;
  font-size: 17px;
  font-weight: 800;
  color: #0a2240;
}
.role-badge {
  display: inline-block;
  background: rgba(0, 61, 165, 0.08);
  color: #003da5;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.8px;
  border-radius: 6px;
  padding: 3px 10px;
  text-transform: uppercase;
}

/* ── INFO ROWS ── */
.info-list {
  display: flex;
  flex-direction: column;
}
.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f4fa;
  font-size: 13.5px;
  gap: 12px;
}
.info-row.no-border {
  border-bottom: none;
}
.info-label {
  color: #6b7f99;
  flex-shrink: 0;
}
.info-value {
  font-weight: 700;
  color: #0a2240;
  text-align: right;
  word-break: break-all;
}
.info-value.mono {
  font-family: monospace;
  font-size: 13px;
}

/* ── STATUS TAGS ── */
.verified-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #16a34a;
  font-size: 13px;
  font-weight: 700;
}
.pending-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #d97706;
  font-size: 13px;
  font-weight: 700;
}

/* ── SECTION DIVIDER (inside KYC+Bank card) ── */
.section-divider {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 20px 0 16px;
  padding-top: 4px;
  border-top: 1px solid #edf2f7;
}
.section-divider i {
  font-size: 14px;
  color: #003da5;
}
.section-divider span {
  font-size: 11px;
  font-weight: 800;
  color: #003da5;
  letter-spacing: 1.2px;
  text-transform: uppercase;
}

/* ── BANK ── */
.pending-bank-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fffbeb;
  border: 1px solid rgba(234, 179, 8, 0.3);
  border-radius: 10px;
  padding: 13px;
  margin-bottom: 14px;
}
.pending-bank-notice > i {
  font-size: 18px;
  color: #d97706;
  flex-shrink: 0;
  margin-top: 1px;
}
.notice-title {
  margin: 0 0 3px;
  font-size: 13px;
  font-weight: 700;
  color: #92400e;
}
.notice-sub {
  margin: 0;
  font-size: 12px;
  color: #b45309;
  line-height: 1.5;
}
.verify-bank-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 20px;
  background: #003da5;
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s;
}
.verify-bank-btn:hover {
  background: #0033a0;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 34, 64, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 2000;
}
.modal-card {
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 520px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #dce7f6;
  box-shadow: 0 18px 40px rgba(10, 34, 64, 0.2);
  padding: 16px;
}
.modal-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.modal-title {
  margin: 0;
  font-size: 18px;
  color: #0a2240;
}
.bank-modal-form {
  margin-bottom: 0;
}
.bank-modal-loading {
  position: absolute;
  inset: 0;
  z-index: 4;
  background: rgba(255, 255, 255, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(1px);
}
.bank-modal-loading-inner {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #0a2240;
  font-size: 14px;
  font-weight: 700;
}
.bank-modal-loading-inner p {
  margin: 0;
}
.loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #c7d6ec;
  border-top-color: #003da5;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

/* ── FORMS ── */
.form-box {
  background: #f8fafc;
  border: 1px solid #dce7f6;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 11px;
}
.fgroup {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.fgroup label {
  font-size: 12px;
  font-weight: 700;
  color: #0a2240;
}
.fgroup input {
  height: 40px;
  padding: 0 12px;
  font-size: 13px;
  color: #333f48;
  border: 1px solid rgba(10, 34, 64, 0.12);
  border-radius: 8px;
  background: #fff;
  outline: none;
  transition: all 0.2s;
  font-family: inherit;
  box-sizing: border-box;
  width: 100%;
}
.fgroup input:focus {
  border-color: #003da5;
  box-shadow: 0 0 0 3px rgba(0, 61, 165, 0.08);
}
.form-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 2px;
}
.cancel-btn {
  height: 36px;
  padding: 0 18px;
  background: transparent;
  border: 1.5px solid #d1dbe8;
  color: #6b7f99;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
}
.cancel-btn:hover {
  border-color: #003da5;
  color: #003da5;
}
.save-btn {
  height: 36px;
  padding: 0 20px;
  background: #ffc72c;
  border: none;
  color: #0a2240;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
}
.save-btn:hover:not(:disabled) {
  background: #003da5;
  color: #fff;
}
.save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── ADDRESS ── */
.addr-display {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #f8fafc;
  border: 1px solid #edf2f7;
  border-radius: 12px;
  padding: 14px;
}
.addr-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: rgba(0, 61, 165, 0.07);
  color: #003da5;
  font-size: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.addr-text {
  flex: 1;
}
.addr-name {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 800;
  color: #0a2240;
}
.addr-line {
  margin: 0 0 3px;
  font-size: 12.5px;
  font-weight: 500;
  color: #5f6f85;
}
.addr-meta {
  margin: 0;
  font-size: 12.5px;
  color: #6b7f99;
  line-height: 1.5;
}
.primary-pill {
  font-size: 10px;
  font-weight: 800;
  color: #0a2240;
  background: #ffc72c;
  border-radius: 999px;
  padding: 3px 10px;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  align-self: flex-start;
}

/* ── BENEFICIARIES ── */
.beneficiary-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.beneficiary-api-error {
  margin-bottom: 10px;
}
.beneficiary-item {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #e2eaf6;
  border-radius: 10px;
  padding: 12px 14px;
  background: #fafcff;
}
.beneficiary-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(0, 61, 165, 0.08);
  color: #003da5;
  font-size: 13px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.beneficiary-info {
  flex: 1;
  min-width: 0;
}
.b-name {
  margin: 0 0 2px;
  font-size: 13px;
  font-weight: 700;
  color: #0a2240;
}
.b-detail-row {
  display: grid;
  grid-template-columns: 92px 1fr;
  gap: 6px;
  align-items: start;
}
.b-key {
  margin: 0;
  font-size: 12px;
  color: #64748b;
  font-weight: 700;
}
.b-val {
  margin: 0;
  font-size: 12px;
  color: #6b7f99;
  overflow-wrap: anywhere;
}
.icon-btn {
  width: 30px;
  height: 30px;
  border: 1px solid #e2eaf6;
  background: #f8fafc;
  color: #6b7f99;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.18s;
  flex-shrink: 0;
}
.icon-btn.danger:hover {
  border-color: #dc2626;
  color: #dc2626;
  background: #fff4f4;
}

/* ── MISC ── */
.empty-state {
  text-align: center;
  padding: 28px 12px;
  color: #94a3b8;
}
.empty-state i {
  font-size: 28px;
  display: block;
  margin-bottom: 8px;
}
.empty-state p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
}
.req {
  color: #dc2626;
}
.field-error {
  margin: 0;
  font-size: 12px;
  color: #dc2626;
  font-weight: 600;
}
.field-warning {
  margin: 0;
  font-size: 12px;
  color: #b45309;
  font-weight: 700;
}
.spin-icon {
  animation: spin 0.75s linear infinite;
}
.bank-status-message {
  margin: 8px 0 0;
  font-size: 12px;
  font-weight: 700;
  color: #166534;
}

/* ── TRANSITIONS ── */
.slide-down-enter-active {
  transition: all 0.3s ease;
}
.slide-down-leave-active {
  transition: all 0.2s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── RESPONSIVE ── */
@media (max-width: 860px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 600px) {
  .profile-content {
    padding: 24px 0 48px;
    gap: 14px;
  }
  .page-title {
    font-size: 22px;
  }
  .card {
    padding: 18px;
  }
  .form-row-2 {
    grid-template-columns: 1fr;
  }
  .addr-display {
    flex-wrap: wrap;
  }
  .primary-pill {
    order: -1;
  }
}
</style>
